import React, { useState, useEffect } from "react";
import { useFeedback } from "../../contexts/FeedbackContext";
import { useDashboardUI } from "../../context/GlobalContext";
import incidentService from "../../services/incidentService";
import { toast } from "react-toastify";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Card } from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  RefreshCcw,
  Filter,
  MoreVertical,
  TriangleAlert,
  User,
  Eye,
  X,
  ChevronDown,
  ChevronRight,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { formatUtcToLocal } from "../../helpers/helpers";

// Status badge config
const STATUS_CONFIG = {
  reported:               { label: "Reported",              cls: "bg-gray-100 text-gray-500" },
  assigned_to_facility:   { label: "Assigned to Facility",  cls: "bg-blue-50 text-blue-600" },
  assigned_to_expert:     { label: "Assigned to Expert",    cls: "bg-violet-100 text-violet-600 font-semibold" },
  in_progress:            { label: "In Progress",           cls: "bg-gray-100 text-gray-500" },
  resolved:               { label: "Resolved",              cls: "bg-green-100 text-green-600" },
  closed:                 { label: "Closed",                cls: "bg-gray-50 text-gray-300" },
};

// Priority badge config
const PRIORITY_CONFIG = {
  low:      { label: "Low",      cls: "bg-green-100 text-green-600" },
  medium:   { label: "Medium",   cls: "bg-gray-100 text-gray-500" },
  high:     { label: "High",     cls: "bg-orange-100 text-orange-600" },
  critical: { label: "Critical", cls: "bg-red-50 text-red-500 font-semibold" },
};

// Allowed status transitions for action menu
const STATUS_TRANSITIONS = {
  reported:             ["assigned_to_facility", "assigned_to_expert"],
  assigned_to_facility: ["assigned_to_expert", "in_progress"],
  assigned_to_expert:   ["in_progress"],
  in_progress:          ["resolved"],
  resolved:             ["closed"],
  closed:               [],
};

// Creator to show next to "by": the name when it's given, otherwise the email.
const getCreatedBy = (incident) =>
  incident?.created_by_name?.trim() ||
  incident?.created_by_email ||
  (typeof incident?.created_by === "string" && incident.created_by.includes("@") ? incident.created_by : "");

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, cls: "bg-gray-100 text-gray-500" };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const cfg = PRIORITY_CONFIG[priority] || { label: priority, cls: "bg-gray-100 text-gray-500" };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium whitespace-nowrap ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// Linked Feedback Section 
const LinkedFeedbackSection = ({ incidentId }) => {
  const [state, setState] = useState({ items: [], loading: true, error: false });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const result = await incidentService.getIncidentLinkedFeedback(incidentId);
        setState({ items: result.items || [], loading: false, error: false });
      } catch {
        setState({ items: [], loading: false, error: true });
      }
    };
    fetch();
  }, [incidentId]);

  const formatCategory = (cat) =>
    cat ? cat.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : null;

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
        <RefreshCcw className="h-3.5 w-3.5 animate-spin shrink-0" />
        Loading linked feedback…
      </div>
    );
  }

  if (state.error || state.items.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2.5 text-xs text-gray-400">
        {state.error ? "Failed to load linked feedback." : "No linked feedback found."}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-[#2B3674]" />
          <span className="text-xs font-semibold text-gray-700">
            {state.items.length} feedback item{state.items.length !== 1 ? "s" : ""} linked
          </span>
          {/* Type counts */}
          <span className="flex gap-1">
            {state.items.filter(i => i.feedback_type === "thumbs_down").length > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium border bg-red-100 text-red-700 border-red-200">
                <ThumbsDown className="h-2.5 w-2.5" />
                {state.items.filter(i => i.feedback_type === "thumbs_down").length > 1 &&
                  <span className="ml-0.5">{state.items.filter(i => i.feedback_type === "thumbs_down").length}</span>
                }
              </span>
            )}
            {state.items.filter(i => i.feedback_type === "thumbs_up").length > 0 && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium border bg-green-100 text-green-700 border-green-200">
                <ThumbsUp className="h-2.5 w-2.5" />
                {state.items.filter(i => i.feedback_type === "thumbs_up").length > 1 &&
                  <span className="ml-0.5">{state.items.filter(i => i.feedback_type === "thumbs_up").length}</span>
                }
              </span>
            )}
          </span>
        </div>
        {expanded
          ? <ChevronDown className="h-4 w-4 text-gray-400" />
          : <ChevronRight className="h-4 w-4 text-gray-400" />
        }
      </button>

      {/* Expanded items */}
      {expanded && (
        <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {state.items.map((item, index) => (
            <div key={item.id} className="px-3 py-3 bg-white">
              {/* Item header row */}
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <span className="text-xs font-semibold text-gray-400 tabular-nums">#{index + 1}</span>
                <span className="text-sm font-medium text-gray-900">{item.user_name}</span>
                {item.user_email && (
                  <span className="text-xs text-gray-400">{item.user_email}</span>
                )}
                <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-xs font-medium border ${
                  item.feedback_type === "thumbs_up"
                    ? "bg-green-100 text-green-700 border-green-200"
                    : "bg-red-100 text-red-700 border-red-200"
                }`}>
                  {item.feedback_type === "thumbs_up"
                    ? <><ThumbsUp className="h-2.5 w-2.5" /> Positive</>
                    : <><ThumbsDown className="h-2.5 w-2.5" /> Negative</>
                  }
                </span>
                {formatCategory(item.category) && (
                  <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                    {formatCategory(item.category)}
                  </span>
                )}
                {/* Addressed status pill */}
                <span className={`ml-auto inline-flex items-center gap-1 text-xs font-medium ${
                  item.is_addressed ? "text-green-600" : "text-amber-600"
                }`}>
                  {item.is_addressed
                    ? <><CheckCircle2 className="h-3 w-3" />Addressed</>
                    : <><Clock className="h-3 w-3" />Pending</>
                  }
                </span>
              </div>

              {/* Comment */}
              {item.comment ? (
                <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border-l-2 border-[#2B3674]/30 leading-relaxed">
                  {item.comment}
                </p>
              ) : (
                <p className="text-xs text-gray-300 italic">No comment</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Component
const IncidentTable = () => {
  const {
    incidentList, setIncidentList,
    incidentLoading, setIncidentLoading,
    incidentError, setIncidentError,
    incidentPagination, setIncidentPagination,
    incidentFilters, setIncidentFilters,
    isSuperAdmin,
    canReassignIncidents,
    refreshTrigger,
    triggerRefresh,
    clearFeedbackSelection,
  } = useFeedback();

  const { selectedFacilityFilter } = useDashboardUI();

  const [showFilters, setShowFilters] = useState(false);
  // Page-by-page pagination — the API only exposes a forward cursor, so we
  // keep the cursor that led to each page we've already visited and reuse it
  // to go back, instead of re-requesting from the start.
  const [page, setPage] = useState(1);
  const [cursorStack, setCursorStack] = useState([null]);
  const [viewModal, setViewModal] = useState({ isOpen: false, incident: null });
  const [reassignModal, setReassignModal] = useState({
    isOpen: false, incidentId: null, incidentTitle: "",
    availableUsers: [], selectedUserId: "", assignmentNote: "", loading: false,
  });

  // Data fetching
  const fetchIncidentsList = async (cursor = null, reset = false) => {
    try {
      setIncidentLoading(true);
      setIncidentError(null);
      const filters = {
        ...incidentFilters,
        cursor,
        facilityId: isSuperAdmin && selectedFacilityFilter?.facility_number
          ? selectedFacilityFilter.facility_number : null,
      };
      const result = await incidentService.getIncidentsList(filters);
      if (reset || !cursor) setIncidentList(result.items);
      else setIncidentList((prev) => [...prev, ...result.items]);
      setIncidentPagination({ total: result.total, has_more: result.has_more, next_cursor: result.next_cursor });
    } catch {
      setIncidentError("Failed to load incidents list");
      toast.error("Failed to load incidents list");
    } finally {
      setIncidentLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setIncidentFilters((prev) => ({ ...prev, [key]: value === "all" ? "" : value }));
  };

  const clearFilters = () => {
    setIncidentFilters({ status: "", assignedUserId: "", facilityId: null, limit: 20 });
  };

  const hasActiveFilters = !!incidentFilters.status || !!incidentFilters.assignedUserId;

  // Status update
  const handleStatusUpdate = async (incidentId, newStatus) => {
    try {
      await incidentService.updateIncidentStatus(incidentId, { status: newStatus, notes: "" });
      toast.success("Incident status updated");
      setPage(1); setCursorStack([null]);
      fetchIncidentsList(null, true);
    } catch {
      toast.error("Failed to update incident status");
    }
  };

  // Reassign
  const openReassignModal = async (incident) => {
    setReassignModal({
      isOpen: true, incidentId: incident.id, incidentTitle: incident.title,
      availableUsers: [], selectedUserId: "", assignmentNote: "", loading: true,
    });
    try {
      const facilityId = incident.facility_id || selectedFacilityFilter?.facility_number;
      if (facilityId) {
        const usersData = await incidentService.getFacilityUsers(facilityId);
        setReassignModal((prev) => ({ ...prev, availableUsers: usersData.users || [], loading: false }));
      } else {
        setReassignModal((prev) => ({ ...prev, loading: false }));
        toast.error("Cannot determine facility for reassignment");
      }
    } catch {
      setReassignModal((prev) => ({ ...prev, loading: false }));
      toast.error("Failed to load available users");
    }
  };

  const closeReassignModal = () => {
    setReassignModal({
      isOpen: false, incidentId: null, incidentTitle: "",
      availableUsers: [], selectedUserId: "", assignmentNote: "", loading: false,
    });
  };

  const handleReassignSubmit = async () => {
    if (!reassignModal.selectedUserId) { toast.error("Please select a user"); return; }
    if (!reassignModal.assignmentNote.trim()) { toast.error("Please provide an assignment note"); return; }
    try {
      await incidentService.reassignIncident(reassignModal.incidentId, {
        assigned_user_id: reassignModal.selectedUserId,
        assignment_note: reassignModal.assignmentNote,
      });
      toast.success("Incident reassigned successfully");
      setPage(1); setCursorStack([null]);
      fetchIncidentsList(null, true);
      closeReassignModal();
    } catch {
      toast.error("Failed to reassign incident");
    }
  };

  useEffect(() => {
    setPage(1);
    setCursorStack([null]);
    fetchIncidentsList(null, true);
  }, [incidentFilters, selectedFacilityFilter, refreshTrigger]);

  const goToNextPage = () => {
    if (!incidentPagination.has_more || !incidentPagination.next_cursor || incidentLoading) return;
    const nextCursor = incidentPagination.next_cursor;
    fetchIncidentsList(nextCursor, true);
    setCursorStack((prev) => [...prev, nextCursor]);
    setPage((p) => p + 1);
  };

  const goToPrevPage = () => {
    if (page <= 1 || incidentLoading) return;
    const prevCursor = cursorStack[page - 2] ?? null;
    fetchIncidentsList(prevCursor, true);
    setCursorStack((prev) => prev.slice(0, page - 1));
    setPage((p) => p - 1);
  };

  const COLUMNS = [
    { label: "Title",       width: "300px" },
    { label: "Status",      width: "175px" },
    { label: "Priority",    width: "115px" },
    { label: "Assigned To", width: "170px" },
    { label: "Created",     width: "190px" },
    { label: "Updated",     width: "140px" },
    { label: "",            width: "48px"  },
  ];

  return (
    <div className="flex flex-col h-full min-h-0 gap-2">

      <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 min-h-0 flex flex-col bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
      {/* Header (inside the card, as in the design) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 shrink-0 px-5 py-4">
        <div className="flex w-full items-center gap-3 sm:w-auto">
          <div>
            <h3 className="text-base font-bold text-[#14122B]">Incidents</h3>
            <p className="text-xs text-[#9AA1B2] mt-0.5">Tracked issues created from user feedback</p>
          </div>
          {isSuperAdmin && selectedFacilityFilter?.facility_name && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
              {selectedFacilityFilter.facility_name}
            </span>
          )}
          {/* Phones: refresh icon at the top right of the table (the top Refresh button is hidden on this tab) */}
          <button
            type="button"
            onClick={() => {
              clearFeedbackSelection();
              triggerRefresh();
            }}
            disabled={incidentLoading}
            title="Refresh"
            aria-label="Refresh"
            className="ml-auto inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-gray-100 bg-white text-slate-700 shadow-sm hover:border-gray-300 transition-colors disabled:opacity-60 sm:hidden"
          >
            <RefreshCcw className={`h-4 w-4 ${incidentLoading ? "animate-spin" : ""}`} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Filters toggle */}
          <Button
            onClick={() => setShowFilters(!showFilters)}
            size="sm"
            variant={showFilters ? "default" : "outline"}
            className={`gap-1.5 transition-all duration-150 ${
              showFilters
                ? "bg-[#2B3674] text-white border-[#2B3674] hover:bg-[#1a2456]"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900 hover:border-gray-300"
            }`}
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <span className={`h-2 w-2 rounded-full ${showFilters ? "bg-white/80" : "bg-[#2B3674]"}`} />
            )}
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 border-y border-gray-100 p-4 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Status */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Status</label>
              <Select
                value={incidentFilters.status || "all"}
                onValueChange={(v) => handleFilterChange("status", v)}
              >
                <SelectTrigger className="bg-white border-gray-200 text-sm h-9 hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                  {[
                    { value: "all", label: "All statuses" },
                    ...Object.entries(STATUS_CONFIG).map(([v, c]) => ({ value: v, label: c.label })),
                  ].map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assignment */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Assignment</label>
              <Select
                value={incidentFilters.assignedUserId || "all"}
                onValueChange={(v) => handleFilterChange("assignedUserId", v)}
              >
                <SelectTrigger className="bg-white border-gray-200 text-sm h-9 hover:border-gray-300 hover:bg-gray-50 transition-colors focus:ring-1 focus:ring-[#2B3674]/20 focus:border-[#2B3674]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-lg p-1">
                  {[
                    { value: "all", label: "All assignments" },
                    { value: "me",  label: "My Assignments"  },
                  ].map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-sm rounded-md cursor-pointer hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors">
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

          </div>
          {hasActiveFilters && (
            <div className="flex justify-end mt-3">
              <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors cursor-pointer">
                Clear all filters
              </button>
            </div>
          )}
        </div>
      )}

      {/* Table */}
        <div className="table-scroll flex-1 min-h-0" style={{ overflowY: "auto", overflowX: "auto" }}>
          <Table className="border-collapse w-full min-w-[1100px] xl:min-w-[900px]" style={{ tableLayout: "fixed" }}>
            <TableHeader>
              <TableRow className="border-b border-gray-100 hover:bg-indigo-50">
                {COLUMNS.map(({ label, width }) => (
                  <TableHead
                    key={label || "actions"}
                    style={{
                      position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20,
                      verticalAlign: "middle", width, minWidth: width, textAlign: "left",
                    }}
                    className="text-xs font-semibold text-gray-500 uppercase tracking-wide py-3.5 px-4"
                  >
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>

            <TableBody>
              {incidentLoading && incidentList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <RefreshCcw className="h-5 w-5 animate-spin" />
                      <span className="text-sm">Loading incidents…</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : incidentError ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-red-500">
                      <TriangleAlert className="h-6 w-6" />
                      <span className="text-sm">{incidentError}</span>
                    </div>
                  </TableCell>
                </TableRow>
              ) : incidentList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-16">
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                      <Filter className="h-5 w-5" />
                      <span className="text-sm">No incidents found</span>
                      {hasActiveFilters && (
                        <button onClick={clearFilters} className="text-xs text-[#2B3674] hover:underline cursor-pointer">Clear filters</button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                incidentList.map((incident) => {
                  const transitions = STATUS_TRANSITIONS[incident.status] || [];
                  return (
                    <TableRow
                      key={incident.id}
                      className="border-b border-gray-100 last:border-0 hover:bg-gray-50/80 transition-colors"
                    >
                      <TableCell className="px-4 py-3 overflow-hidden" style={{ verticalAlign: "middle", maxWidth: 0 }}>
                        <div className="font-semibold text-sm text-gray-900 leading-snug truncate" title={incident.title}>{incident.title}</div>
                        {incident.description && (
                          <div className="text-sm text-gray-400 mt-0.5 leading-snug truncate" title={incident.description}>{incident.description}</div>
                        )}
                      </TableCell>

                      <TableCell className="px-4 py-3" style={{ verticalAlign: "middle" }}>
                        <StatusBadge status={incident.status} />
                      </TableCell>

                      <TableCell className="px-4 py-3" style={{ verticalAlign: "middle" }}>
                        <PriorityBadge priority={incident.priority} />
                      </TableCell>

                      <TableCell className="px-4 py-3" style={{ verticalAlign: "middle" }}>
                        <div className="flex items-center gap-2">
                          <div className="h-7 w-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500 shrink-0">
                            {(incident.assigned_user_name || "U")
                              .split(" ")
                              .filter(Boolean)
                              .slice(0, 2)
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()}
                          </div>
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {incident.assigned_user_name || "Unassigned"}
                          </span>
                        </div>
                      </TableCell>

                      <TableCell className="px-4 py-3" style={{ verticalAlign: "middle" }}>
                        <div className="text-sm font-medium text-gray-900 whitespace-nowrap">
                          {formatUtcToLocal(incident.created_at, "MMM DD, YYYY")}
                        </div>
                        {getCreatedBy(incident) && (
                          <div className="text-xs text-gray-400 mt-0.5 whitespace-nowrap truncate" title={`by ${getCreatedBy(incident)}`}>by {getCreatedBy(incident)}</div>
                        )}
                      </TableCell>

                      <TableCell className="px-4 py-3" style={{ verticalAlign: "middle" }}>
                        <span className="text-sm text-gray-700 whitespace-nowrap">
                          {incident.updated_at
                            ? formatUtcToLocal(incident.updated_at, "MMM DD, YYYY")
                            : <span className="text-gray-300">—</span>
                          }
                        </span>
                      </TableCell>

                      {/* Actions */}
                      <TableCell style={{ verticalAlign: "middle" }} className="px-2">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors cursor-pointer">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-xl rounded-lg min-w-[190px] p-1">
                            <DropdownMenuItem
                              onClick={() => setViewModal({ isOpen: true, incident })}
                              className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-gray-700 hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors"
                            >
                              <Eye className="h-4 w-4 shrink-0" />
                              View Details
                            </DropdownMenuItem>

                            {transitions.length > 0 && <DropdownMenuSeparator className="bg-gray-100 my-1" />}
                            {transitions.map((status) => (
                              <DropdownMenuItem
                                key={status}
                                onClick={() => handleStatusUpdate(incident.id, status)}
                                className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-blue-700 hover:bg-blue-50 focus:bg-blue-50 outline-none transition-colors"
                              >
                                <ChevronRight className="h-4 w-4 shrink-0" />
                                <span>Move to</span>
                                <span className="ml-auto">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${STATUS_CONFIG[status]?.cls || ""}`}>
                                    {STATUS_CONFIG[status]?.label || status}
                                  </span>
                                </span>
                              </DropdownMenuItem>
                            ))}

                            {/* Reassign */}
                            {canReassignIncidents && (
                              <>
                                <DropdownMenuSeparator className="bg-gray-100 my-1" />
                                <DropdownMenuItem
                                  onClick={() => openReassignModal(incident)}
                                  className="flex items-center gap-2.5 px-3 py-2 text-sm rounded-md cursor-pointer text-gray-700 hover:bg-gray-100 focus:bg-gray-100 outline-none transition-colors"
                                >
                                  <User className="h-4 w-4 shrink-0" />
                                  Reassign
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Pagination (page-by-page, same layout as Prompt Management) */}
      <div className="mt-auto pt-2 pb-2 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm shrink-0">
        <span>Total {incidentPagination.total}</span>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            Rows per page
            <select
              value={incidentFilters.limit || 20}
              onChange={(e) => setIncidentFilters((prev) => ({ ...prev, limit: Number(e.target.value) }))}
              className="rounded-md border px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button disabled={page <= 1 || incidentLoading} onClick={goToPrevPage} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl">‹</button>

            <span>
              {incidentPagination.total === 0
                ? "0 / 0"
                : `${page} / ${Math.max(1, Math.ceil(incidentPagination.total / (incidentFilters.limit || 20)))}`}
            </span>

            <button disabled={!incidentPagination.has_more || incidentLoading} onClick={goToNextPage} className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl">›</button>
          </div>
        </div>
      </div>
      </div>

      {/* View Incident Modal */}
      {viewModal.isOpen && viewModal.incident && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setViewModal({ isOpen: false, incident: null }); }}
        >
          <Card className="w-full max-w-2xl shadow-2xl bg-white rounded-2xl overflow-hidden flex flex-col max-h-[90vh]">

            {/* Pinned header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 shrink-0">
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-[#2B3674]" />
                <h2 className="text-lg font-semibold text-gray-900">Incident Details</h2>
              </div>
              <Button
                variant="ghost" size="sm"
                onClick={() => setViewModal({ isOpen: false, incident: null })}
                className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Scrollable body */}
            <div className="px-6 pb-2 overflow-y-auto flex-1 space-y-4">

              {/* Title */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Title</label>
                <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100">
                  {viewModal.incident.title}
                </p>
              </div>

              {/* Description */}
              {viewModal.incident.description && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Description</label>
                  <p className="text-sm text-gray-900 bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 whitespace-pre-wrap">
                    {viewModal.incident.description}
                  </p>
                </div>
              )}

              {/* Status / Priority / Assigned */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "Status",      content: <StatusBadge status={viewModal.incident.status} /> },
                  { label: "Priority",    content: <PriorityBadge priority={viewModal.incident.priority} /> },
                  {
                    label: "Assigned To",
                    content: (
                      <div className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-sm text-gray-700">{viewModal.incident.assigned_user_name || "Unassigned"}</span>
                      </div>
                    ),
                  },
                ].map(({ label, content }) => (
                  <div key={label}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
                    <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100">{content}</div>
                  </div>
                ))}
              </div>

              {/* Linked Feedback — replaces the dumb count badge */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Linked Feedback</label>
                <LinkedFeedbackSection incidentId={viewModal.incident.id} />
              </div>

              {/* Created / Updated */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Created</label>
                  <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100">
                    <div className="text-sm text-gray-900">
                      {viewModal.incident.created_at ? formatUtcToLocal(viewModal.incident.created_at) : "N/A"}
                    </div>
                    {getCreatedBy(viewModal.incident) && (
                      <div className="text-xs text-gray-400 mt-0.5">by {getCreatedBy(viewModal.incident)}</div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Last Updated</label>
                  <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100">
                    <div className="text-sm text-gray-900">
                      {viewModal.incident.updated_at ? formatUtcToLocal(viewModal.incident.updated_at) : "Not updated"}
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Pinned footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-100 bg-white shrink-0">
              <Button
                variant="outline"
                onClick={() => setViewModal({ isOpen: false, incident: null })}
                className="text-gray-600 hover:bg-gray-100"
              >
                Close
              </Button>
            </div>

          </Card>
        </div>
      )}

      {/* Reassign Modal */}
      {reassignModal.isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeReassignModal(); }}
        >
          <div className="w-full max-w-md">
            <Card className="shadow-2xl bg-white rounded-2xl">
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-[#2B3674]" />
                      <h2 className="text-lg font-semibold text-gray-900">Reassign Incident</h2>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 ml-7 line-clamp-1">"{reassignModal.incidentTitle}"</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={closeReassignModal} className="h-8 w-8 p-0 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md">
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-4">
                  {/* User select */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Assign to User <span className="text-red-400">*</span>
                    </label>
                    {reassignModal.loading ? (
                      <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
                        <RefreshCcw className="h-4 w-4 animate-spin" />Loading users…
                      </div>
                    ) : (
                      <Select
                        value={reassignModal.selectedUserId}
                        onValueChange={(v) => setReassignModal((prev) => ({ ...prev, selectedUserId: v }))}
                      >
                        <SelectTrigger className="bg-white border-gray-200 text-sm h-9">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent className="bg-white border border-gray-200 shadow-lg">
                          {reassignModal.availableUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="font-medium">{user.name}</div>
                              <div className="text-xs text-gray-400">{user.email}</div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {!reassignModal.loading && reassignModal.availableUsers.length === 0 && (
                      <p className="text-xs text-gray-400 mt-1">No users available for assignment</p>
                    )}
                  </div>

                  {/* Assignment note */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Assignment Note <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2B3674]/20 focus:border-[#2B3674] resize-none"
                      rows="3"
                      placeholder="Explain the reason for reassignment…"
                      value={reassignModal.assignmentNote}
                      onChange={(e) => setReassignModal((prev) => ({ ...prev, assignmentNote: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
                  <Button variant="outline" onClick={closeReassignModal} className="text-gray-600 hover:bg-gray-100">Cancel</Button>
                  <Button
                    onClick={handleReassignSubmit}
                    disabled={!reassignModal.selectedUserId || !reassignModal.assignmentNote.trim()}
                    className="bg-[#2B3674] hover:bg-[#1E2951] text-white"
                  >
                    Reassign Incident
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentTable;